import React from 'react';
import Hotkeys from 'react-hot-keys';
import { Form, Formik } from 'formik';
import clsx from 'clsx';

import { SVGNormalCheck } from '@assets/SVGNormalCheck';
import RenderIf from '@components/RenderIf';
import {
  Button,
  FormControl,
  Grid,
  Typography,
  useMediaQuery,
} from '@material-ui/core';

import { getLocalStorage, isEmptyObject, setLocalStorage } from '@utils/index';
import useStyles from './styles';
import { StepsProps } from '@components/VideoScriptGenerator/types';
import FooterSteps from '@components/VideoScriptGenerator/Steps/FooterSteps';
import { Theme } from '@styles/theme/theme';
import { ValuesScriptData } from '@components/VideoScriptGenerator/Steps/StepsData';

interface SelectorStepProps extends StepsProps {
  data: ValuesScriptData;
  values: any;
}

const SelectorStep = ({
  next,
  back,
  position,
  data,
  values,
}: SelectorStepProps) => {
  const classes = useStyles();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('xs')
  );

  const onSubmit = (value: any) => {
    if (data.populate) {
      setLocalStorage(data.valueName, value[data.valueName]);
    }

    next(value);
  };

  const getInitValue = () => {
    let initValue = values[data.valueName] || data?.options?.[0]?.value;
    if (data.populate) {
      const populate = getLocalStorage(data.valueName);
      initValue = populate || initValue;
    }
    return initValue;
  };

  return (
    <Formik
      initialValues={{
        [data.valueName || '']: getInitValue(),
      }}
      validationSchema={data.schemeValidation}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, errors, handleChange, handleSubmit }) => (
        <Form className={classes.form}>
          <Grid
            container
            className={classes.selectorContainer}
            direction="column"
            justify="center"
            alignItems="flex-start"
          >
            <Typography className={classes.title} variant="h2">
              {data.title}
            </Typography>
            <FormControl component="fieldset" className={classes.fieldset}>
              {data?.options?.map((option) => (
                <Hotkeys
                  key={option.label}
                  keyName={option.shortcut}
                  onKeyUp={() =>
                    handleChange({
                      target: { name: data.valueName, value: option.value },
                    })
                  }
                >
                  <Button
                    key={option.label}
                    className={clsx(classes.buttonSelector, {
                      [classes.selected]:
                        option.value === values[data.valueName || ''],
                    })}
                    onClick={() =>
                      handleChange({
                        target: { name: data.valueName, value: option.value },
                      })
                    }
                  >
                    <Grid
                      container
                      direction="row"
                      justify="space-between"
                      alignItems="flex-start"
                      style={{ gap: 20, maxWidth: '524px', width: '100%' }}
                    >
                      <span>
                        <RenderIf condition={!isMobile}>
                          <div className={classes.shortcut}>
                            {option.shortcut}
                          </div>
                        </RenderIf>
                        {option.label}
                      </span>
                      {option.value === values[data.valueName] && (
                        <SVGNormalCheck />
                      )}
                    </Grid>
                  </Button>
                </Hotkeys>
              ))}
            </FormControl>
            <Hotkeys key="next" keyName="enter" onKeyUp={() => handleSubmit()}>
              <FooterSteps
                back={back}
                position={position}
                available={!isEmptyObject(errors)}
              />
            </Hotkeys>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default SelectorStep;
